import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import isEqual from 'fast-deep-equal';
import clsx from 'clsx';
import { Skeleton } from '@material-ui/lab';
import { useMediaQuery, Grid, Typography, Button } from '@material-ui/core';

// CONSTANTS
import { CardTypes, ViewTypes } from '@/constants/cards';
import { ROUTE_PROFILE_SUBSCRIPTION_DETAIL, ROUTE_SUBSCRIPTION } from '@/constants/routes';

// UTILITY
import {
  analyticsInternalLinks,
  analyticsSwimlaneNavigation,
  externalLink,
  getExternalUrlParams,
} from '@/utility/analytics';
import { updateCarouselOptions } from '@/utility/content';
import { updateUrl } from '@/utility/common';
import { getLink, routeEnhance } from '@/utility/routes';

// HOC
import { useGuard } from '@/hoc/Guard';

// COMPONENTS
import SectionTitleLink from '@/components/SectionTitleLink';
import StageCarousel from '@/components/Carousels/StageSwiperCarousel';
import { SwiperSlide } from '@/components/Carousels/SwiperCarousel';
import AppCardWithTooltip from '@/components/Cards/AppCardWithTooltip';
// card without tooltip
import AppCard from '@/components/Cards/AppCardWithTooltip/help/AppCard';
import AppCardSkeleton from '@/components/Skeletons/AppCardSkeleton';
import Link from '@/components/Link/Link';

// HELP
import ConciergeButton from '@/containers/StreamingRewards/help/AppCard/help/ConciergeButton';

// STYLES
import { Container, CarouselWrapper } from './styles';

const cardTypes = Object.values(CardTypes);
const viewTypes = Object.values(ViewTypes);

const AppsCarousel = props => {
  const {
    data,
    className,
    title,
    link,
    stylesType,
    showSeeAll,
    loading,
    analyticsLocation,
    onUpdateStatus,
    onRemoveStatus,
    onSelectClick,
    swimlaneAnalytics,
    hideTitle,
    hideMobileNav,
    cardType,
    viewType,
  } = props;
  const [t] = useTranslation();
  const navigate = useNavigate();

  const downMd = useMediaQuery(({ breakpoints }) => breakpoints.down('md'));
  const downXs = useMediaQuery(({ breakpoints }) => breakpoints.down('xs'));
  const prevActiveIndexRef = React.useRef(0);
  const { unique_id, anonymousId, isSimpleView, partner, isIframe, card } = useSelector(state => ({
    unique_id: state.auth.unique_id,
    anonymousId: state.auth.anonymousId,
    isSimpleView: state.app.isSimpleView,
    partner: state.partner.data,
    isIframe: state.app.isIframe,
    card: state.auth?.user?.card || {},
  }));
  const guard = useGuard({
    iframe: false,
  });

  const carouselOptions = React.useMemo(() => {
    let options = {
      spaceBetween: 30,
      slidesPerView: 4,
      watchOverflow: true,
      virtual: {
        enabled: true,
      },
      breakpoints: {
        0: {
          slidesPerView: 1.5,
          slidesPerGroup: 1,
          spaceBetween: 16,
        },
        475: {
          slidesPerView: 2,
          slidesPerGroup: 2,
        },
        750: {
          slidesPerView: 3,
          slidesPerGroup: 3,
        },
        960: {
          slidesPerView: 4,
          slidesPerGroup: 4,
          allowTouchMove: false,
        },
      },
    };

    if (viewType === ViewTypes.SR) {
      options = {
        ...options,
        breakpoints: {
          ...options.breakpoints,
          960: {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
          1100: {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
          1400: {
            slidesPerView: 4,
            slidesPerGroup: 4,
          },
        },
      };
    }

    if (hideMobileNav) {
      options = updateCarouselOptions(options);
    }

    return options;
  }, [hideMobileNav, cardType]);

  const showTitle = React.useMemo(() => {
    return Boolean(title && !hideTitle && !(cardType === CardTypes.SIMPLE));
  }, [title, hideTitle, cardType]);

  const hasApps = !!data.length;
  const hasSwimlaneAnalytics = !!Object.keys(swimlaneAnalytics).length;
  const hasPartner = !!partner.slug;
  const partnerSettings = partner?.settings || {};

  let useSubscription = true;

  if (hasPartner) {
    useSubscription = !!partnerSettings.use_authorizing && !!partnerSettings.use_subscription;
  }

  let titleLinkProps = {};
  let cardLinkProps = {};

  if (hasSwimlaneAnalytics) {
    titleLinkProps = {
      analyticsType: 'swimlane-see-all',
      analyticsProps: {
        ...swimlaneAnalytics,
        type: 'title',
      },
    };
    cardLinkProps = {
      analyticsType: 'swimlane-see-all',
      analyticsProps: {
        ...swimlaneAnalytics,
        type: 'card',
      },
    };
  }

  let updatedStylesType = stylesType;

  if (downMd && updatedStylesType === 'collections' && !hideMobileNav) {
    updatedStylesType = 'want-to-watch';
  }

  const skeletons = new Array(downXs ? 2 : 4).fill('');
  const skeletonsSection = (
    <>
      {!hideTitle && (
        <Grid item xs={12}>
          <Skeleton width="40%" height={50} />
        </Grid>
      )}
      {skeletons.map((item, index) => {
        return (
          <Grid item xs={6} sm={3} key={index}>
            <AppCardSkeleton type="new-card" />
          </Grid>
        );
      })}
    </>
  );

  function onSlideChange(swiper) {
    const activeIndex = swiper.activeIndex;
    const prevActiveIndex = prevActiveIndexRef.current;

    const properties = {
      ...swimlaneAnalytics,
      items_shifted: Math.abs(prevActiveIndex - activeIndex),
    };

    analyticsSwimlaneNavigation(properties);

    prevActiveIndexRef.current = activeIndex;
  }

  const handleVisitSite = (e, appData) => {
    e.preventDefault();
    e.stopPropagation();
    const externalUrlParams = getExternalUrlParams({ unique_id: unique_id || anonymousId });
    const href = updateUrl(appData.url, externalUrlParams);

    externalLink({
      location: 'app hover',
      href,
      app_id: data.id,
      type: 'app',
      service_name: appData.name,
      is_affiliate: !!appData.is_affiliate,
      partner: partner?.partner_slug,
      partner_id: partner?.partner_id,
    });

    if (typeof window !== 'undefined') {
      window.open(href, '_blank');
    }
  };

  const handleSelectClick = (e, appData, balance) => {
    e.preventDefault();
    e.stopPropagation();

    onSelectClick(appData, balance);
  };

  function handleSubscribeOrManage(isSubscribed, slug) {
    const isUnsubscribedInIframe = isIframe && !isSubscribed;
    const routeEnhanced = routeEnhance(
      getLink(isSubscribed ? ROUTE_PROFILE_SUBSCRIPTION_DETAIL : ROUTE_SUBSCRIPTION, { slug }),
      { state: { location: analyticsLocation }, withoutIframe: isUnsubscribedInIframe },
    );

    analyticsInternalLinks({
      unique_id: routeEnhanced.state.unique_id,
      from_page_path: routeEnhanced.state.from_page_path,
      from_search_params: routeEnhanced.state.from_search_params,
      to_page_path: routeEnhanced.pathname,
      to_search_params: routeEnhanced.search,
      partner_id: routeEnhanced.state.partner_id,
      location: routeEnhanced.state.location,
    });

    if (isUnsubscribedInIframe) {
      window.open(
        `${routeEnhanced.pathname}?${routeEnhanced.search}`,
        '_blank',
        'noopener,noreferrer',
      );
    } else {
      navigate(routeEnhanced);
    }
  }

  const renderCard = React.useCallback(
    (appData, card_index) => {
      const isSubscribed = appData.user_subscription;
      const subscriptionIsAvailable =
        useSubscription && appData.has_subscription && !isSimpleView && guard;

      if (cardType === CardTypes.SIMPLE) {
        return (
          <AppCard
            data={appData}
            onUpdateStatus={onUpdateStatus}
            onRemoveStatus={onRemoveStatus}
            location={`Slot ${card_index + 1}`}
            swimlaneAnalytics={{ ...swimlaneAnalytics, card_index }}
            hideControls
          >
            <div className="m-t-20">
              {subscriptionIsAvailable ? (
                <Button
                  className="app-card-content__visit-site"
                  variant="outlined"
                  color="primary"
                  size="medium"
                  fullWidth
                  onClick={() => handleSubscribeOrManage(isSubscribed, appData.unique_slug)}
                  startIcon={!isSubscribed && <i className="icon-mb-icon-2" />}
                >
                  {isSubscribed ? t('buttons.manage') : t('buttons.subscribe')}
                </Button>
              ) : (
                <Button
                  className="app-card-content__visit-site"
                  variant="outlined"
                  color="primary"
                  size="medium"
                  fullWidth
                  onClick={e => handleVisitSite(e, appData)}
                >
                  {appData.external_button_text ?? t('buttons.visitSite')}
                </Button>
              )}
            </div>
          </AppCard>
        );
      }

      if (cardType === CardTypes.SR || cardType === CardTypes.SRCONCIERGE) {
        return (
          <AppCard
            className="app-card--sr"
            data={appData}
            location={`Slot ${card_index + 1}`}
            swimlaneAnalytics={{ ...swimlaneAnalytics, card_index }}
            hideControls
            showSubscribedMarker
          >
            <div className="m-t-20">
              {cardType === CardTypes.SRCONCIERGE ? (
                <ConciergeButton
                  data={appData}
                  onClick={e => handleSelectClick(e, appData, card.balance)}
                />
              ) : (
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  size="medium"
                  fullWidth
                  disabled={isSubscribed}
                  onClick={e => handleSelectClick(e, appData, card.balance)}
                  startIcon={subscriptionIsAvailable && <i className="icon-mb-icon-2" />}
                >
                  {t('buttons.select')}
                </Button>
              )}
            </div>
          </AppCard>
        );
      }

      return (
        <AppCardWithTooltip
          data={appData}
          onUpdateStatus={onUpdateStatus}
          onRemoveStatus={onRemoveStatus}
          location={`Slot ${card_index + 1}`}
          swimlaneAnalytics={{ ...swimlaneAnalytics, card_index }}
        />
      );
    },
    [cardType, swimlaneAnalytics.swimlane_id, card.balance],
  );

  return (
    <Container className={clsx('carousel-apps', `carousel-apps--${updatedStylesType}`, className)}>
      {loading ? (
        <Grid container spacing={2} className="carousel-services__loading-container">
          {skeletonsSection}
        </Grid>
      ) : (
        hasApps && (
          <>
            {showTitle && (
              <SectionTitleLink
                className="carousel-apps__title"
                title={title}
                link={link}
                analyticsLocation={analyticsLocation}
                LinkProps={titleLinkProps}
              />
            )}
            <CarouselWrapper>
              <StageCarousel
                carouselOptions={{
                  ...carouselOptions,
                  onSlideChange,
                }}
                stylesType={updatedStylesType}
              >
                <>
                  {data.map((appData, card_index) => (
                    <SwiperSlide
                      className="card-wrapper"
                      key={card_index}
                      virtualIndex={card_index}
                    >
                      {renderCard(appData, card_index)}
                    </SwiperSlide>
                  ))}
                  {showSeeAll && !!link && data.length >= 4 && (
                    <SwiperSlide
                      className="card-wrapper card-wrapper--see-all"
                      virtualIndex={data.length}
                    >
                      <div className="dummy-card">
                        <Link
                          className="dummy-card__content"
                          to={link}
                          location={analyticsLocation}
                          {...cardLinkProps}
                        >
                          <i className="icon-arrow-right" />
                          <Typography className="p4" component="span">
                            {t('buttons.seeAll')}
                          </Typography>
                        </Link>
                      </div>
                    </SwiperSlide>
                  )}
                </>
              </StageCarousel>
            </CarouselWrapper>
          </>
        )
      )}
    </Container>
  );
};

AppsCarousel.defaultProps = {
  data: [],
  className: '',
  title: '',
  link: '',
  stylesType: 'collections',
  loading: false,
  showSeeAll: false,
  onUpdateStatus: () => {},
  onRemoveStatus: () => {},
  onSelectClick: () => {},
  swimlaneAnalytics: {},
  hideTitle: false,
  hideMobileNav: false,
  cardType: 'default',
  viewType: 'default',
};

AppsCarousel.propTypes = {
  data: PropTypes.array,
  className: PropTypes.string,
  title: PropTypes.string,
  link: PropTypes.string,
  stylesType: PropTypes.string,
  loading: PropTypes.bool,
  analyticsLocation: PropTypes.string,
  showSeeAll: PropTypes.bool,
  onUpdateStatus: PropTypes.func,
  onRemoveStatus: PropTypes.func,
  onSelectClick: PropTypes.func,
  swimlaneAnalytics: PropTypes.object,
  hideTitle: PropTypes.bool,
  hideMobileNav: PropTypes.bool,
  cardType: PropTypes.oneOf(cardTypes),
  viewType: PropTypes.oneOf(viewTypes),
};

export default React.memo(AppsCarousel, isEqual);
